<template>
  <iDropdown
    :teleport-to-body="false"
    :show-dropdown="showColorPicker"
    :width="width"
    :overlay-position="myOverlayPosition"
    :z-index="3"
    @close="resetAndClose"
  >
    <template #trigger>
      <slot name="trigger" :open="() => showColorPicker = true">
        <iRow
          :id="myIndicatorId"
          vertical-align="middle"
          :style-overrides="{
            paddingLeft: 'small',
            paddingRight: 'none',
            borderWidth: 'standard',
            borderColor: 'dark',
            borderRadius: 'large',
            paddingTop: 'extraSmall',
            paddingBottom: 'extraSmall',
            backgroundColor: 'paper',
            hoverBackgroundColor: 'subtle',
          }"
          :width="width"
          align="between"
          wrap="nowrap"
          @click.stop="showColorPicker = true"
        >
          <iColumn align="left" vertical-align="middle">
            <iText
              font-size="small"
              ellipsis
              :max-width="myTextMaxWidth"
              :wrap="false"
            >
              {{ camelToTitleCase(myTitle) }}
            </iText>
          </iColumn>

          <iColumn
            :style-overrides="{
              marginRight: 'small',
              borderWidth: 'thin',
              borderColor: 'dark',
              borderRadius: 'standard',

            }"
            align="right"
            width="hug"
          >
            <ColorSwatch
              :color="myColor"
              :width="19"
            />
          </iColumn>
        </iRow>
      </slot>
    </template>
    <template #overlay>
      <iColumn
        v-drag="'#color-picker-header'"
        class="color-picker-body-wrapper"
        gap="none"
        :width="300"
        :style-overrides="{
          backgroundColor: 'paper',
          borderRadius: 'large',
          borderWidth: 'thin',
          borderColor: 'dark'
        }"
      >
        <iRow
          id="color-picker-header"
          class="header"
          position="relative"
          vertical-align="middle"
          align="center"
          :style-overrides="{
            paddingTop: 'extraSmall',
            paddingBottom: 'extraSmall'
          }"
        >
          <iButton
            class="close-modal-button"
            size="extraSmall"
            variant="tertiary"
            text-display="inline-flex"
            :style-overrides="{
              extraSmallPaddingTop: 'none',
              extraSmallPaddingBottom: 'none',
              extraSmallPaddingLeft: 'none',
              extraSmallPaddingRight: 'none',
            }"
            @click.stop="resetAndClose"
          >
            <iIcon icon="close" size="small" />
          </iButton>
          <iText font-size="large">
            Colors - {{ camelToTitleCase(context) }}
          </iText>
        </iRow>
        <iDivider />
        <iRow
          v-if="colorPickerTypes.length > 1"
          class="navigation"
          align="between"
          vertical-align="middle"
          :style-overrides="{
            paddingTop: 'extraSmall',
            paddingBottom: 'extraSmall',
            paddingLeft: 'standard',
            paddingRight: 'standard',
          }"
        >
          <iButton
            class="previous-panel-btn"
            size="extraSmall"
            variant="text"
            :class="{
              'button-with-icon-left': currentColorPickerTypeIndex - 1 >= 0
            }"
            @click="goToPreviousPanel"
          >
            <iRow
              vertical-align="middle"
              width="hug"
              gap="extraSmall"
              wrap="nowrap"
            >
              <iIcon
                v-if="currentColorPickerTypeIndex - 1 >= 0"
                icon="caret-left"
                variant="subtle"
              />
              <iText :variant="!previousColorPickerType ? 'subtle' : 'standard'">
                {{
                  !previousColorPickerType ? colorContext : previousColorPickerType.label
                }}
              </iText>
            </iRow>
          </iButton>
          <iButton
            class="next-panel-btn"
            size="extraSmall"
            variant="text"
            :class="{
              'button-with-icon-right': currentColorPickerTypeIndex + 1 < colorPickerTypes.length
            }"
            @click="goToNextPanel"
          >
            <iRow
              vertical-align="middle"
              width="hug"
              gap="extraSmall"
              wrap="nowrap"
            >
              <iText :variant="!nextColorPickerType ? 'subtle' : 'standard'">
                {{ !nextColorPickerType ? "Raw" : nextColorPickerType.label }}
              </iText>
              <iIcon
                v-if="currentColorPickerTypeIndex + 1 < colorPickerTypes.length"
                icon="caret-right"
                variant="subtle"
              />
            </iRow>
          </iButton>
        </iRow>
        <iDivider v-if="colorPickerTypes.length > 1" />
        <iRow
          v-if="colorColumnLabel"
          class="column-headers"
          align="between"
          :style-overrides="{
            paddingTop: 'extraSmall',
            paddingBottom: 'extraSmall',
            paddingLeft: 'standard',
            paddingRight: 'standard',
          }"
          vertical-align="middle"
          wrap="nowrap"
        >
          <iColumn>
            <iText>
              Alias
            </iText>
          </iColumn>
          <iColumn :align="colorColumnLabel === 'Variant' ? 'center' : 'right'">
            <iText>
              {{ colorColumnLabel }}
            </iText>
          </iColumn>
        </iRow>
        <iColumn class="color-container-wrappers" :style-overrides="{paddingBottom: 'extraSmall'}">
          <ThemeColorPicker
            v-if="tempType==='theme'"
            v-model="value"
            :context="context"
            :context-theme-id="publicationThemeId"
            :type="sourceType"
            @update:model-value="selectValue"
          />
          <SemanticColorPicker
            v-else-if="tempType==='semantic'"
            v-model="value"
            :context="context"
            :context-theme-id="publicationThemeId"
            :type="sourceType"
            @update:model-value="selectValue"
          />
          <PrimitiveColorPicker
            v-else-if="tempType==='palette'"
            v-model="value"
            :context="context"
            :theme-id-context="publicationThemeId"
            :type="sourceType"
            :allow-transparent="allowTransparent"
            @update:model-value="selectValue"
          />
          <iCustomColorPicker
            v-else-if="tempType==='custom'"
            v-model="value"
            :context="context"
            :type="sourceType"
            @update:model-value="selectValue"
          />
        </iColumn>
      </iColumn>
    </template>
  </iDropdown>
</template>

<script>
import { camelToTitleCase } from "@bloglovin/vue-framework";
import { useStyleStore } from "@vcl";
import { uniqueId } from "lodash";
import { mapActions, mapState } from "pinia";

import PrimitiveColorPicker from "%/components/color-picker/PrimitiveColorPicker.vue";
import ThemeColorPicker from "%/components/color-picker/ThemeColorPicker.vue";
import SemanticColorPicker from "%/components/color-picker/SemanticColorPicker.vue";
import ColorSwatch from "%/components/color-picker/ColorSwatch.vue";
import { Enum } from "@vcl";
import { useStagingFrameStore } from "~/stores/staging-frame-store";

export default {
  name: "ColorPicker",
  components: {
    PrimitiveColorPicker,
    ThemeColorPicker,
    SemanticColorPicker,
    ColorSwatch,
  },
  props: {
    colorContext: {
      type: String,
      default: "",
    },
    modelValue: {
      type: Object,
      required: true,
    },
    hideThemePicker: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideSemanticPicker: {
      type: Boolean,
      required: false,
      default: false,
    },
    hidePalettePicker: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideCustomPicker: {
      type: Boolean,
      required: false,
      default: false,
    },
    width: {
      type: [Enum, Number],
      required: false,
      default: "fill",
      options: ["hug", "fill"],
    },
    allowTransparent: {
      type: Boolean,
      default: false,
    },
    popupPosition: {
      type: Enum,
      required: false,
      default: "default",
      options: ["bottom", "default"],
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      internalModelValue: null,
      value: this.internalModelValue?.value,
      sourceType: "",
      context: "",
      showColorPicker: false,
      myUniqueId: uniqueId("color-picker-"),
      myIndicatorId: uniqueId("color-indicator-"),
      tempType: "",
      customValue: "#000000",
      myOverlayPositionY: 0,
      myOverlayPositionX: 0,
    };
  },
  computed: {
    ...mapState(useStyleStore, ["semanticPalette", "themePalette", "themes"]),
    colorPickerTypes() {
      const types = [
        { label: camelToTitleCase(this.colorContext), key: "theme" },
        { label: "Theme Palette", key: "semantic" },
        { label: "Primitives", key: "palette" },
        { label: "Raw", key: "custom" },
      ];

      return types.filter(type => {
        if (type.key === "theme" && this.hideThemePicker) {
          return false;
        }
        if (type.key === "semantic" && this.hideSemanticPicker) {
          return false;
        }
        if (type.key === "palette" && this.hidePalettePicker) {
          return false;
        }
        if (type.key === "custom" && this.hideCustomPicker) {
          return false;
        }
        return true;
      });
    },
    colorColumnLabel() {
      const currentColorPanelKey = this.colorPickerTypes[this.currentColorPickerTypeIndex].key;
      switch (currentColorPanelKey) {
        case "theme":
          return "Color Value";
        case "semantic":
          return "Variant";
        default:
          return "";
      }
    },
    publicationThemeId() {
      return useStagingFrameStore().themeId;
    },
    myTheme() {
      return this.themes[this.publicationThemeId];
    },
    myTextMaxWidth() {
      if (this.width === "fill" || this.width === "hug") {
        return 300;
      }
      return this.width - 50;
    },
    mySource() {
      return this.tempType || this.sourceType;
    },
    myColor() {
      if (this.sourceType === "theme") {
        let { source, value } = this.getColorSourceAndValue(this.myTheme.color[this.context], this.value);
        return this.getColorFromSource(source, value, this.publicationThemeId);
      }
      return this.getColorFromSource(this.sourceType, this.value, this.publicationThemeId);
    },
    myTitle() {
      if (this.sourceType === "theme") {
        return this.value;
      } else if (this.sourceType === "palette") {
        return this.value.split(".")[0] + " " + this.value.split(".")[1];
      } else if (this.sourceType === "semantic") {
        return this.value.split(".")[0] + " " + this.value.split(".")[1];
      } else {
        return "Custom";
      }
    },
    currentColorPickerTypeIndex() {
      return this.colorPickerTypes.findIndex(({ key }) => key.includes(this.tempType));
    },
    previousColorPickerType() {
      return this.colorPickerTypes[this.currentColorPickerTypeIndex - 1];
    },
    nextColorPickerType() {
      return this.colorPickerTypes[this.currentColorPickerTypeIndex + 1];
    },
    myOverlayPosition() {
      return `${this.myOverlayPositionY}-${this.myOverlayPositionX}`;
    },
    shouldShowAPicker() {
      /* eslint-disable */
      return this.sourceType === "theme" && this.hideThemePicker ||
          this.sourceType === "semantic" && this.hideSemanticPicker ||
          this.sourceType === "palette" && this.hidePalettePicker ||
          this.sourceType === "custom" && this.hideCustomPicker
      /* eslint-enable */
    },
    resolvedDefaultColor() {
      if (this.colorContext === "Backgrounds") {
        return {
          source: "palette.background",
          value: "transparent.100",
        };
      } else if (this.colorContext === "Borders") {
        return {
          source: "theme.border",
          value: "standard",
        };
      } else {
        return {
          source: "theme.font",
          value: "standard",
        };
      }
    },
  },
  watch: {
    modelValue: {
      handler(newValue) {
        if (!newValue) {
          this.internalModelValue = this.resolvedDefaultColor;
          return;
        }
        this.internalModelValue = newValue;
      },
      immediate: true,
    },
    internalModelValue: {
      handler(newValue) {
        this.value = newValue.value;
      },
      immediate: true,
    },
    showColorPicker() {
      let left = window.innerWidth - 305;
      left -= useStagingFrameStore().rightPanelWidth;

      let top = 0;
      const dropdownIndicatorElement = document.querySelector(`#${this.myIndicatorId}`);
      if (dropdownIndicatorElement) {
        const dropdownIndicatorElementRect = dropdownIndicatorElement.getBoundingClientRect();
        top = dropdownIndicatorElementRect.bottom;
        if (this.popupPosition === "bottom") {
          left = dropdownIndicatorElementRect.left;
        }
      }

      this.myOverlayPositionY = top;
      this.myOverlayPositionX = left;
    },
  },
  mounted() {
    if (!this.internalModelValue.source) {
      throw new Error("ColorPicker: source is required");
    }
    this.sourceType = this.internalModelValue.source.split(".")[0];
    this.context = this.internalModelValue.source.split(".")[1];

    if (this.shouldShowAPicker) {
      this.tempType = this.colorPickerTypes.length > 0 ? this.colorPickerTypes[0].key : this.sourceType;
    } else {
      this.tempType = this.sourceType;
    }

    if (this.sourceType === "custom") {
      this.customValue = this.value;
    }
  },
  methods: {
    ...mapActions(useStyleStore, ["getColorFromSource", "getColorSourceAndValue"]),
    camelToTitleCase,
    selectValue(value) {
      this.value = value;
      this.sourceType = this.colorPickerTypes.find(({ key }) => key.includes(this.mySource)).key;
      this.$emit("update:modelValue", {
        "source": this.sourceType + "." + this.context,
        "value": value,
      });
    },
    goToPreviousPanel() {
      if (this.currentColorPickerTypeIndex - 1 >= 0) {
        this.tempType = this.previousColorPickerType.key;
      }
    },
    goToNextPanel() {
      if (this.currentColorPickerTypeIndex + 1 < this.colorPickerTypes.length) {
        this.tempType = this.nextColorPickerType.key;
      }
    },
    resetAndClose() {
      // `modelValue` is typed `required: true` but in practice arrives
      // as null when the inspector tool is mounted before its upstream
      // option has hydrated (the Color Palette page's Paper Background
      // dropdown opens this picker while
      // `publicationTheme.color.background.paper` is still resolving
      // — particularly after the basic-theme structural fallback
      // engages on a fresh destination). Bail before the dereference
      // so closing the picker without a hydrated model doesn't throw
      // "Cannot read properties of null (reading 'value')" and leave
      // the dropdown stuck open.
      if (!this.modelValue) {
        this.showColorPicker = false;
        return;
      }
      if (this.value === this.modelValue.value) {
        this.showColorPicker = false;
        return;
      }
      this.sourceType = this.internalModelValue.source.split(".")[0];
      this.context = this.internalModelValue.source.split(".")[1];

      if (this.shouldShowAPicker) {
        this.tempType = this.colorPickerTypes.length > 0 ? this.colorPickerTypes[0].key : this.sourceType;
      } else {
        this.tempType = this.sourceType;
      }

      this.$emit("update:modelValue", {
        "source": this.sourceType + "." + this.context,
        "value": this.value,
      });
      this.showColorPicker = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.close-modal-button {
  position: absolute;
  left: 8px;
  top: 8px;
  padding: 2px !important;
}

.button-with-icon-left {
  position: relative;
  right: 6px;
}

.button-with-icon-right {
  position: relative;
  left: 6px;
}

:deep(.i-dropdown-content) {
  box-shadow: -2px 3px 6px grey;
}
</style>
